/**
 * Warenkorb-Schublade (Mini-Cart).
 *
 * Progressive enhancement only: every element this script hooks into already works without
 * JavaScript (product form posts to /cart/add and reloads; the header cart icon links to
 * {{ routes.cart_url }}; "Entfernen" is a real link to item.url_to_remove). This script
 * intercepts those actions with fetch() so the customer never leaves the page, and re-renders
 * the drawer body/footer from the server (Shopify Section Rendering API,
 * ?section_id=cart-drawer) -- the exact same Liquid the page used on first load, not a second
 * client-side template.
 */
(function () {
  "use strict";

  var drawer = document.querySelector("[data-cart-drawer]");
  if (!drawer) return;

  var body = document.getElementById("cart-drawer-body");
  var footer = document.getElementById("cart-drawer-footer");
  var lastFocused = null;

  function reducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function open() {
    lastFocused = document.activeElement;
    drawer.setAttribute("data-open", "");
    drawer.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    var closeBtn = drawer.querySelector(".cart-drawer__close");
    if (closeBtn) closeBtn.focus({ preventScroll: true });
    document.addEventListener("keydown", onKeydown);
  }

  function close() {
    drawer.removeAttribute("data-open");
    drawer.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", onKeydown);
    if (lastFocused && typeof lastFocused.focus === "function") {
      lastFocused.focus({ preventScroll: true });
    }
  }

  function onKeydown(event) {
    if (event.key === "Escape") close();
  }

  // A65 (Strategie 04.10.2026): "Warenkorb ansehen" is a real link with data-cart-drawer-close. Before, the handler
  // below called preventDefault() for every [data-cart-drawer-close] and so swallowed the navigation to /cart.
  // Now a link with a real target closes the drawer AND navigates; only the X button / backdrop just close.
  // In the theme preview the link keeps ?preview_theme_id=… so /cart opens in the same preview.
  function keepPreview(link) {
    var pv = new URLSearchParams(window.location.search).get("preview_theme_id");
    if (!pv || !/^\d+$/.test(pv)) return;
    try {
      var u = new URL(link.getAttribute("href"), window.location.href);
      if (u.origin !== window.location.origin || u.searchParams.has("preview_theme_id")) return;
      u.searchParams.set("preview_theme_id", pv);
      link.setAttribute("href", u.pathname + u.search + u.hash);
    } catch (e) {}
  }

  drawer.addEventListener("click", function (event) {
    var link = event.defaultPrevented ? null : event.target.closest("a[href]");
    if (link && drawer.contains(link) && !link.hasAttribute("data-cart-drawer-remove")) {
      var href = link.getAttribute("href") || "";
      if (href && href.charAt(0) !== "#" && href.indexOf("javascript:") !== 0) {
        keepPreview(link);
        if (link.hasAttribute("data-cart-drawer-close")) close();
        return; // let the browser follow the link
      }
    }
    if (event.target.closest("[data-cart-drawer-close]")) {
      event.preventDefault();
      close();
    }
  });

  document.addEventListener("click", function (event) {
    var opener = event.target.closest("[data-cart-drawer-open]");
    if (opener) {
      event.preventDefault();
      open();
    }
  });

  function updateHeaderCount(count) {
    document.querySelectorAll(".site-header__cart-count").forEach(function (el) {
      if (el.hasAttribute("data-wishlist-count")) return;
      if (count > 0) {
        el.textContent = String(count);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    // The visible badge above is aria-hidden (it's purely decorative for sighted users);
    // the accessible count lives in this link's aria-label and needs updating in lockstep,
    // or screen readers keep announcing the server-rendered count from first page load.
    document.querySelectorAll("[data-cart-count-template]").forEach(function (link) {
      var template = link.getAttribute("data-cart-count-template");
      if (template) {
        link.setAttribute("aria-label", template.replace("%%COUNT%%", String(count)));
      }
    });
  }

  function refreshDrawer() {
    var url = window.location.pathname + (window.location.pathname.indexOf("?") > -1 ? "&" : "?") + "section_id=cart-drawer";
    return fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var newBody = doc.getElementById("cart-drawer-body");
        var newFooter = doc.getElementById("cart-drawer-footer");
        if (newBody && body) body.innerHTML = newBody.innerHTML;
        if (newFooter && footer) footer.innerHTML = newFooter.innerHTML;
      });
  }

  function showError() {
    if (!footer) return;
    var existing = footer.querySelector("[data-cart-drawer-error]");
    if (existing) return;
    var note = document.createElement("p");
    note.className = "cart-drawer__note";
    note.setAttribute("data-cart-drawer-error", "");
    note.style.color = "var(--gold)";
    note.textContent = window.cartDrawerStrings && window.cartDrawerStrings.error ? window.cartDrawerStrings.error : "Could not update the cart.";
    footer.prepend(note);
  }

  // A54 (04.10.2026): "Clear cart" in drawer and on the cart page -- /cart/clear.js, then the empty view with a 5 s "Undo"
  // (re-adds the saved lines via /cart/add.js). No browser dialog.
  var undoTimer = null;
  document.addEventListener("click", function (event) {
    var clearBtn = event.target.closest("[data-cart-clear]");
    if (!clearBtn) return;
    event.preventDefault();
    var saved = [];
    clearBtn.disabled = true;
    fetch("/cart.js").then(function (r) { return r.json(); }).then(function (cart) {
      saved = cart.items.map(function (it) {
        var o = { id: it.variant_id, quantity: it.quantity };
        if (it.properties && Object.keys(it.properties).length) o.properties = it.properties;
        return o;
      });
      return fetch("/cart/clear.js", { method: "POST", headers: { Accept: "application/json" } });
    }).then(function () {
      updateHeaderCount(0);
      return refreshDrawer();
    }).then(function () {
      showUndo(saved);
    }).catch(function () { clearBtn.disabled = false; showError(); });
  });

  function showUndo(saved) {
    var tpl = document.querySelector("[data-cart-undo-tpl]");
    if (!tpl) return;
    var page = document.querySelector("[data-buy-cart] .buy-cart__form");
    var host = page ? page.parentNode : body;
    if (page) {
      var empty = document.createElement("div");
      empty.className = "buy-cart__empty";
      empty.innerHTML = '<span class="buy-cart__emptyicon" aria-hidden="true"></span><b></b><div class="buy-acts"><a class="btn btn--gold" href="/pages/product"></a></div>';
      empty.querySelector("b").textContent = tpl.content.querySelector("span").textContent;
      var cont = document.querySelector('[data-cart-drawer-body] .cart-drawer__empty a');
      empty.querySelector("a").textContent = cont ? cont.textContent : "→";
      if (cont) empty.querySelector("a").href = cont.getAttribute("href");
      page.replaceWith(empty);
      host = empty;
    }
    if (!host) return;
    var note = tpl.content.firstElementChild.cloneNode(true);
    host.appendChild(note);
    if (undoTimer) clearTimeout(undoTimer);
    undoTimer = setTimeout(function () { note.remove(); }, 5000);
    note.querySelector("[data-cart-undo]").addEventListener("click", function () {
      clearTimeout(undoTimer);
      if (!saved.length) { note.remove(); return; }
      fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: saved }) })
        .then(function () {
          if (page) { window.location.reload(); return; }
          note.remove();
          return fetch("/cart.js").then(function (r) { return r.json(); }).then(function (cart) { updateHeaderCount(cart.item_count); return refreshDrawer(); });
        })
        .catch(showError);
    });
  }

  // A45 (04.10.2026): small public hook so other scripts (product-v60.js, cart with accessories) can refresh and open the drawer.
  window.CryonosCartDrawer = {
    // A65: refresh header count and drawer content without opening the drawer (watchlist "Add all to cart" shows its own message).
    update: function () {
      return fetch("/cart.js").then(function (r) { return r.json(); }).then(function (cart) {
        updateHeaderCount(cart.item_count);
        return refreshDrawer();
      });
    },
    refresh: function () {
      return fetch("/cart.js").then(function (r) { return r.json(); }).then(function (cart) {
        updateHeaderCount(cart.item_count);
        return refreshDrawer();
      }).then(function () { open(); });
    }
  };

  // Intercept "Add to cart" on any product form (product.liquid's {% form 'product' %}).
  document.addEventListener("submit", function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    var isAddToCart = form.getAttribute("data-type") === "add-to-cart" || /\/cart\/add(\.js)?(\?|$)/.test(form.action);
    if (!isAddToCart) return;

    var submitter = event.submitter;
    if (submitter && submitter.name !== "add") return; // only the real add-to-cart button, not other buttons sharing the form

    event.preventDefault();
    var formData = new FormData(form);
    if (submitter) formData.set(submitter.name, submitter.value || "1");
    if (submitter) submitter.disabled = true;

    fetch("/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: formData,
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      })
      .then(function () {
        return fetch("/cart.js").then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        updateHeaderCount(cart.item_count);
        return refreshDrawer();
      })
      .then(function () {
        open();
      })
      .catch(function () {
        // Sold out / validation error from Shopify: fall back to a normal submit so the
        // customer still sees Shopify's own error message instead of a silent failure.
        form.submit();
      })
      .finally(function () {
        if (submitter) submitter.disabled = false;
      });
  });

  // Quantity change / remove inside the drawer.
  if (body) {
    body.addEventListener("change", function (event) {
      var input = event.target.closest("[data-cart-drawer-qty]");
      if (!input) return;
      changeLine(input.getAttribute("data-line-key"), input.value);
    });

    body.addEventListener("click", function (event) {
      var removeLink = event.target.closest("[data-cart-drawer-remove]");
      if (!removeLink) return;
      event.preventDefault();
      changeLine(removeLink.getAttribute("data-line-key"), 0);
    });
  }

  function changeLine(key, quantity) {
    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: Number(quantity) }),
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        updateHeaderCount(cart.item_count);
        return refreshDrawer();
      })
      .catch(function () {
        showError();
      });
  }
})();
